export function normalizeTxHash(raw) {
  const txHash = String(raw || "")
    .trim()
    .replace(/^0x/i, "")
    .toLowerCase();
  if (!/^[0-9a-f]{64}$/.test(txHash)) {
    throw new Error("tx hash must be 64 hex chars");
  }
  return txHash;
}

export function extractBoundParticipantId(message) {
  const m = String(message).match(
    /already bound to participant\s+([0-9a-f-]{36})/i
  );
  return m?.[1] ?? null;
}

/** True when the browser lost the response (CORS-blocked 503, offline, etc.). */
export function isTransientNetworkError(err) {
  const msg = String(err?.message || err || "").toLowerCase();
  const name = String(err?.name || "").toLowerCase();
  return (
    name === "typeerror" ||
    msg.includes("networkerror") ||
    msg.includes("failed to fetch") ||
    msg.includes("network request failed") ||
    msg.includes("load failed") ||
    msg.includes("503") ||
    msg.includes("service temporarily unavailable") ||
    msg.includes("rate limit") ||
    msg.includes("too many requests")
  );
}

export function isRetryableVerifyError(err) {
  const msg = String(err?.message || err || "").toLowerCase();
  if (isTransientNetworkError(err)) return true;
  return (
    msg.includes("not found") ||
    msg.includes("not yet included") ||
    msg.includes("bad gateway") ||
    msg.includes("node lookup failed")
  );
}

/** Prefer `cause` (retry wrapper) and strip the old suffix if still present. */
export function verifyErrorMessage(err) {
  const raw = err?.cause?.message ?? err?.message ?? err;
  return String(raw || "verify failed")
    .replace(/\s*[--]\s*auto-verify stopped.*$/i, "")
    .trim();
}

/**
 * WEN / gateway only index Dusk **testnet**. Mainnet proof txs will never
 * verify here - surface that when the wallet is clearly on the wrong chain.
 */
export function assertWalletOnTestnet(wallet) {
  const chainId = String(wallet?.state?.chainId || "").trim().toLowerCase();
  if (!chainId) return;
  if (chainId === "dusk:2" || chainId.includes("testnet")) return;
  if (chainId === "dusk:1" || chainId.includes("mainnet")) {
    throw new Error(
      "Wallet is on mainnet - switch the Dusk extension to testnet (dusk:2), then send/verify the self-transfer again."
    );
  }
}

export function humanizeVerifyError(err) {
  if (isTransientNetworkError(err) || isTransientNetworkError(err?.cause)) {
    return (
      "Could not reach the gateway (often a short rate-limit blip while polling). " +
      "Wait a few seconds, then Verify again - your tx hash is kept."
    );
  }

  const msg = verifyErrorMessage(err);
  const lower = msg.toLowerCase();

  // Specific gateway / proof errors first - never hide them behind the
  // "auto-verify stopped" wrapper used by verifyParticipantWithRetry.
  if (lower.includes("not found on the network") || lower.includes("unknown transaction")) {
    return (
      "Transaction not found on testnet yet - wait for the next block, then Verify again. " +
      "If you sent the proof while the wallet was on mainnet, switch to testnet and send a new self-transfer."
    );
  }
  if (lower.includes("not yet included")) {
    return "Transaction not included in a block yet - wait a bit, then Verify again.";
  }
  if (lower.includes("already bound")) {
    const id = extractBoundParticipantId(msg);
    if (id) {
      return `This Moonlight account is already bound to participant ${id}. Resume that participant to continue, or switch wallets.`;
    }
    return msg;
  }
  if (lower.includes("already been used") || lower.includes("this transaction has already been used")) {
    return "This transaction was already used as proof for another participant.";
  }
  if (lower.includes("memo does not match")) {
    return "Transaction memo does not match this participant id - send a new self-transfer with the memo shown above.";
  }
  if (lower.includes("self-transfer") || lower.includes("receiver must equal")) {
    return "Proof must be a Moonlight self-transfer (send to your own account).";
  }
  if (lower.includes("mainnet") && lower.includes("testnet")) {
    return msg;
  }
  // Never invent an "auto-verify finished" blurb - that hid real gateway
  // failures (and confused manual Verify again). Surface the cleaned message.
  return msg || "Verify failed";
}

/**
 * Poll verify until included or attempts exhausted.
 *
 * Defaults for auto-verify (`attempts` > 1): wait **one block** (`initialDelayMs`
 * = `delayMs` = 10s) before the first poll - the wallet returns a hash at
 * broadcast time, but GraphQL only sees included txs. Then up to **4 × 10s**
 * polls. That stays under api.wen nginx `limit_req` (10r/m, burst 5).
 * Faster/longer auto-polls overflowed the zone → nginx 503 without CORS →
 * Firefox "NetworkError when attempting to fetch resource."
 *
 * Manual Verify again should pass `attempts: 1` (and thus no initial delay)
 * so the real gateway error surfaces immediately.
 *
 * On exhaustion, rethrows the **last gateway error** with `{ cause, autoVerifyStopped }`
 * so humanizeVerifyError can show the real reason (not a generic auto-verify blurb).
 */
export const DUSK_BLOCK_TIME_MS = 10_000;

export async function verifyParticipantWithRetry({
  verifyFn,
  body,
  attempts = 4,
  delayMs = DUSK_BLOCK_TIME_MS,
  /** Wait before first poll. Default: one block for multi-attempt, 0 for one-shot. */
  initialDelayMs,
  onAttempt,
  onWait,
}) {
  const waitBeforeFirst =
    initialDelayMs !== undefined
      ? initialDelayMs
      : attempts > 1
        ? delayMs
        : 0;
  if (waitBeforeFirst > 0) {
    onWait?.(waitBeforeFirst);
    await new Promise((r) => setTimeout(r, waitBeforeFirst));
  }

  let lastErr;
  for (let i = 0; i < attempts; i++) {
    onAttempt?.(i + 1, attempts);
    try {
      return await verifyFn(body);
    } catch (e) {
      lastErr = e;
      if (!isRetryableVerifyError(e) || i === attempts - 1) break;
      // Extra pause after network/503 so nginx rate tokens can refill.
      const waitMs = isTransientNetworkError(e)
        ? Math.max(delayMs, DUSK_BLOCK_TIME_MS)
        : delayMs;
      await new Promise((r) => setTimeout(r, waitMs));
    }
  }
  if (!lastErr) {
    const err = new Error("verify timed out");
    err.autoVerifyStopped = true;
    throw err;
  }
  const err = new Error(String(lastErr.message || lastErr));
  err.cause = lastErr;
  err.autoVerifyStopped = true;
  throw err;
}
